import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  HeartHandshake,
  ShieldCheck,
  Sprout,
  TrendingUp,
} from "lucide-react";
import { Eyebrow, MaskedLine, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { Logo } from "../components/Logo";
import { SEO } from "../components/SEO";
import { useContent } from "../lib/content";

const VALUE_ICONS = [Sprout, ShieldCheck, TrendingUp, HeartHandshake];

export default function Home() {
  const { content } = useContent();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      <SEO
        title="Rooted Business Advisors | Bookkeeping & Financial Guidance — Greater Cincinnati & Southeast Indiana"
        description="Expert bookkeeping, financial reporting, payroll oversight and business advisory services for small businesses throughout Greater Cincinnati and Southeast Indiana."
      />
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden bg-forest-deep" data-testid="hero-section">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <img
            src="/assets/hero-tree.jpg"
            alt="A mature tree with deep exposed roots at golden hour"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-forest-deep/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/95 via-forest-deep/20 to-forest-deep/30" />

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-40 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <Eyebrow light>{content.hero.eyebrow}</Eyebrow>
          </motion.div>

          <h1 className="mt-8 font-display text-5xl font-medium leading-[0.98] tracking-tight text-cream sm:text-6xl lg:text-[5.5rem]" data-testid="hero-headline">
            <MaskedLine delay={0.3}>{content.hero.line1}</MaskedLine>
            <MaskedLine delay={0.45}>{content.hero.line2}</MaskedLine>
            <MaskedLine delay={0.6} className="italic text-gold">
              {content.hero.line3}
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8 }}
            className="mt-6 text-[11px] font-semibold uppercase tracking-[0.24em] text-gold"
            data-testid="hero-tagline"
          >
            {content.hero.tagline}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-cream/80 md:text-lg"
          >
            {content.hero.subtext}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-cream px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-forest transition-colors duration-300 hover:bg-gold"
            >
              Request a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-secondary-link"
              className="link-underline inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream/85 hover:text-cream"
            >
              Explore Services
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="scroll-cue absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 text-center text-[10px] uppercase tracking-[0.3em] text-cream/60 md:block"
        >
          Scroll
        </motion.div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36" data-testid="about-teaser-section">
        <div className="grid items-center gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <Eyebrow>About Rooted</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-forest md:text-5xl">
                {content.aboutTeaser.heading1}
                <br />
                <span className="italic text-olive">{content.aboutTeaser.heading2}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-md leading-relaxed text-sage">{content.aboutTeaser.copy}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/about"
                data-testid="about-teaser-link"
                className="group mt-10 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-forest"
              >
                <span className="link-underline">Learn More About Us</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-6">
            <div className="relative flex items-center justify-center border border-forest/15 bg-parchment px-10 py-20">
              <div className="absolute left-6 top-6 h-10 w-10 border-l border-t border-gold" />
              <div className="absolute bottom-6 right-6 h-10 w-10 border-b border-r border-gold" />
              <Logo size={190} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-forest/10 bg-cream" data-testid="values-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <Reveal>
            <p className="text-center text-[11px] font-semibold uppercase tracking-[0.3em] text-sage">
              Built on Values. Focused on Your Success.
            </p>
          </Reveal>
          <div className="mt-16 grid gap-px bg-forest/10 sm:grid-cols-2 lg:grid-cols-4">
            {content.values.map((v, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <Reveal key={i} delay={i * 0.1} className="bg-cream">
                  <div className="group flex h-full flex-col gap-5 p-9 transition-colors duration-500 hover:bg-parchment">
                    <Icon className="h-7 w-7 text-gold transition-transform duration-500 group-hover:-translate-y-1" strokeWidth={1.4} />
                    <h3 className="font-display text-xl font-semibold text-forest">{v.title}</h3>
                    <p className="text-sm leading-relaxed text-sage">{v.copy}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-forest text-cream" data-testid="services-preview-section">
        <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <div>
              <Reveal>
                <Eyebrow light>What We Do</Eyebrow>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-6 max-w-xl font-display text-4xl font-medium leading-[1.05] tracking-tight md:text-5xl">
                  Every Number, <span className="italic text-gold">Tended To.</span>
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/services"
                data-testid="services-preview-all-link"
                className="group inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-cream/80 hover:text-cream"
              >
                <span className="link-underline">View All Services</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-16">
            {content.services.map((s, i) => (
              <Reveal key={s.id || i} delay={i * 0.05}>
                <Link
                  to={`/services#${s.id || `service-${i}`}`}
                  data-testid={`service-preview-${s.id || i}`}
                  className="group grid items-center gap-4 border-t border-cream/15 py-8 transition-colors duration-500 last:border-b hover:bg-cream/5 md:grid-cols-12 md:px-6"
                >
                  <span className="font-display text-lg italic text-gold md:col-span-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-2xl font-medium md:col-span-4 md:text-3xl">{s.name}</h3>
                  <p className="text-sm leading-relaxed text-cream/65 md:col-span-6">{s.blurb}</p>
                  <ArrowUpRight className="hidden h-5 w-5 text-gold transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 md:col-span-1 md:block md:justify-self-end" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-forest/10 bg-parchment" data-testid="experience-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <Eyebrow>Proven Results</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-forest md:text-5xl">
              {content.experience.heading}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl leading-relaxed text-sage">{content.experience.intro}</p>
          </Reveal>
          <div className="mt-16 grid gap-px bg-forest/10 sm:grid-cols-2 lg:grid-cols-4">
            {content.experience.items.map((item, i) => (
              <Reveal key={i} delay={i * 0.1} className="bg-parchment">
                <div className="flex h-full flex-col gap-4 p-9 transition-colors duration-500 hover:bg-cream">
                  <span className="font-display text-5xl font-medium tracking-tight text-olive">{item.stat}</span>
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-forest">{item.label}</h3>
                  <p className="text-sm leading-relaxed text-sage">{item.details}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36" data-testid="cta-section">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow>{content.cta.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-forest md:text-5xl">
                {content.cta.heading}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-md leading-relaxed text-sage">{content.cta.copy}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/contact"
                data-testid="cta-section-button"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest"
              >
                Request a Consultation
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border border-gold" />
              <img
                src="/assets/cta-mug.jpg"
                alt="Rooted Business Advisors mug beside a journal"
                className="relative w-full object-cover shadow-[0_8px_30px_rgba(26,54,36,0.12)]"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
